import { z } from "zod";

/**
 * Runtime value of a `file-upload` form field. A field of this type stores an array of these
 * refs in `Form.fields[<fieldId>]` (the existing JSON column — no schema migration). The bytes
 * live in the dedicated form-files S3 bucket; only this lightweight reference is persisted on
 * the form. See the formFiles API routes for upload/download/delete and the access rules.
 */
export const FormFileRefSchema = z.object({
  s3Key: z.string().min(1), // "<officeId>/formFiles/<formId>/<fieldId>/<uuid>-<sanitizedName>"
  fileName: z.string().min(1), // friendly display name
  contentType: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  // Audit hints — optional so a missing client-side value never voids the whole ref.
  uploadedById: z.string().optional(),
  uploadedAt: z.string().optional(), // ISO timestamp
});

export type FormFileRef = z.infer<typeof FormFileRefSchema>;

export const FormFileRefArraySchema = z.array(FormFileRefSchema);

/** Narrow an unknown field value to FormFileRef[] (returns [] if it isn't one). */
export function asFormFileRefs(value: unknown): FormFileRef[] {
  const parsed = FormFileRefArraySchema.safeParse(value);
  return parsed.success ? parsed.data : [];
}

/** True if a field value is a (possibly empty) array of FormFileRefs — used by the PDF/read-only
 * paths to detect a file-upload value by shape (field.type metadata is stripped before render). */
export function isFormFileRefValue(value: unknown): boolean {
  return Array.isArray(value) && FormFileRefArraySchema.safeParse(value).success;
}

/**
 * Render a file-upload field value as a human-readable filename list for the PDF / read-only
 * views (e.g. "scan.pdf, xray.png"). Returns "" for an empty/unparseable value.
 */
export function formFileRefsToDisplayString(value: unknown, separator = ", "): string {
  return asFormFileRefs(value)
    .map((r) => r.fileName)
    .filter(Boolean)
    .join(separator);
}
